import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';
import type { VendorDetails } from '@store/vendors/vendorDetails/types';

import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'addressList',
  'comments',
  'createdBy',
  'createdDate',
  'historyContext',
  'modifiedBy',
  'modifiedDate',
  'operationType',
  'vendorContracts',
]);

const OPERATING_LOCATION_FIELDS = new Set(['operatingLocationList']);

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areVendorDetailsScalarValuesEqual = (
  before: unknown,
  after: unknown,
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  return false;
};

const getVendorDetailsForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const [field] = getPropertyPath(path);

  if (OPERATING_LOCATION_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.vendorDetails
      .vendorDetailsOperatingLocations;
  }

  return CHANGE_TRACKING_SOURCES.vendorDetails.vendorDetailsDetails;
};

const findVendorDetailsChangedForms = (
  before: VendorDetails,
  after: VendorDetails,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areVendorDetailsScalarValuesEqual,
    getForm: (change) => getVendorDetailsForm(change.path),
  });

const vendorDetailsFormChangeTracker = createFormChangeTracker<
  VendorDetails,
  ChangeTrackingSource
>({
  getIdentity: (vendor) => vendor.id || undefined,
  findChangedForms: findVendorDetailsChangedForms,
});

export const setVendorDetailsChangeBaseline =
  vendorDetailsFormChangeTracker.setBaseline;
export const clearVendorDetailsChangeBaseline =
  vendorDetailsFormChangeTracker.clearBaseline;
export const synchronizeVendorDetailsChangeHydration =
  vendorDetailsFormChangeTracker.synchronizeHydration;
export const prepareVendorDetailsChangeSummary =
  vendorDetailsFormChangeTracker.prepare;
